/**
 * Pure helpers for the Ticket drawer (Safety V1 parity, Phase 2 Task 2.8).
 *
 * The weights rule is V1's (editrecord_safety JS 3576-3742 / funcs_safety.pl violationlookup):
 * severity = violation.weight (+2 when the violation is out-of-service), time = 3,
 * total = severity * time. The server recomputes `totticketweight` on save; the client
 * keeps the per-row numbers in sync so what the user sees is what gets posted.
 */
import type { ViolationLookupRow } from '../../../types/safetySystem';

export type ChildRow = Record<string, any> & { _key?: string; recno?: number; deleted?: string };

/** Columns the backend accepts on `body.ticket` (tickets.py TICKET_FIELDS). */
export const TICKET_FIELDS = [
  'incidentrecno', 'ticketnum', 'inspectedby', 'ticketprecinct', 'ticketcounty', 'tickethwy', 'milemarker',
  'driverrecno', 'pronumber', 'ticketstartdate', 'ticketstarttime', 'ticketendtime', 'ticketclean', 'ticketlevel',
  'statecopyrcvddate', 'physcopyrcvddate', 'ticketsentdate', 'dotbonuscreatedate', 'vigillodatetime',
] as const;

export const VIOLATION_FIELDS = [
  'wasticketed', 'violation', 'violationdesc', 'violationunit', 'violationoos', 'severityweight', 'timeweight', 'totalweight',
] as const;

export const EQUIPMENT_FIELDS = [
  'ticketequipunit', 'ticketequiptype', 'ticketequipmake', 'ticketequipco', 'ticketequipplate', 'ticketequipstate',
  'ticketequipvin', 'ticketequipowner',
] as const;

export const OOS_SEVERITY_BUMP = 2;
export const DEFAULT_TIME_WEIGHT = 3;

const ZERO_DATES = new Set(['', '0', '0000-00-00', '0000-00-00 00:00:00', 'None']);
const norm = (v: unknown): string => (v === null || v === undefined ? '' : String(v));
const num = (v: unknown): number => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

/** The editable copy of a ticket: only the postable columns, missing ones as ''. */
export function pickTicketFields(ticket: Record<string, any>): Record<string, any> {
  const out: Record<string, any> = {};
  for (const f of TICKET_FIELDS) out[f] = ticket[f] === null || ticket[f] === undefined ? '' : ticket[f];
  return out;
}

/** Pre-save state of a brand-new ticket (mirrors tickets.py _new_ticket_defaults). */
export function newTicketDefaults(): Record<string, any> {
  const d = pickTicketFields({});
  d.incidentrecno = 0;
  d.inspectedby = 0;
  d.driverrecno = 0;
  d.pronumber = 0;
  d.ticketclean = 'N';
  return d;
}

export const isClosed = (closedatetime: unknown): boolean => !ZERO_DATES.has(norm(closedatetime).trim());

const toMinutes = (t: unknown): number | null => {
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(norm(t).trim());
  if (!m) return null;
  return Number(m[1]) * 60 + Number(m[2]) + (m[3] ? Number(m[3]) / 60 : 0);
};

/** Whole minutes between the start and end inspection times; null when either is blank / zero or end < start. */
export function inspectionMinutes(start: unknown, end: unknown): number | null {
  const a = toMinutes(start);
  const b = toMinutes(end);
  if (a === null || b === null) return null;
  if (a === 0 && b === 0) return null;
  if (b < a) return null;
  return Math.floor(b - a);
}

export function recomputeTotal<T extends ChildRow>(row: T): T {
  const severity = num(row.severityweight);
  const time = num(row.timeweight);
  return { ...row, severityweight: severity, timeweight: time, totalweight: severity * time };
}

/** OOS on adds +2 to the severity, OOS off takes it back (never below 0); total follows. Idempotent. */
export function setViolationOos<T extends ChildRow>(row: T, oos: string): T {
  const was = norm(row.violationoos).toUpperCase() === 'Y';
  const now = norm(oos).toUpperCase() === 'Y';
  let severity = num(row.severityweight);
  if (now && !was) severity += OOS_SEVERITY_BUMP;
  else if (!now && was) severity = Math.max(0, severity - OOS_SEVERITY_BUMP);
  return recomputeTotal({ ...row, violationoos: now ? 'Y' : 'N', severityweight: severity });
}

/** Typeahead pick: code + description + base weights, re-applying the OOS bump when the row is already OOS. */
export function applyViolationLookup<T extends ChildRow>(row: T, hit: ViolationLookupRow): T {
  const oos = norm(row.violationoos).toUpperCase() === 'Y';
  const severity = num(hit.severityweight ?? hit.weight) + (oos ? OOS_SEVERITY_BUMP : 0);
  return recomputeTotal({
    ...row,
    violation: hit.violation,
    violationdesc: hit.violationdesc ?? '',
    severityweight: severity,
    timeweight: num(hit.timeweight) || DEFAULT_TIME_WEIGHT,
  });
}

export const totalTicketWeight = (rows: ChildRow[]): number =>
  rows.filter((r) => norm(r.deleted).toUpperCase() !== 'Y').reduce((sum, r) => sum + num(r.totalweight), 0);

/** A brand-new editable child row: every `fields` key blank, `defaults` applied, plus a unique local key. */
export function newChildRow(fields: readonly string[], defaults: Record<string, unknown> = {}): ChildRow {
  const row: ChildRow = { _key: `new-${Date.now()}-${Math.random().toString(36).slice(2, 7)}` };
  for (const f of fields) row[f] = '';
  return { ...row, ...defaults };
}

export const newViolationRow = (): ChildRow => newChildRow(VIOLATION_FIELDS, {
  wasticketed: 'Y', violationunit: 'D', violationoos: 'N', severityweight: 0, timeweight: DEFAULT_TIME_WEIGHT, totalweight: 0,
});

export const newEquipmentRow = (): ChildRow => newChildRow(EQUIPMENT_FIELDS, { ticketequipowner: 'SELF' });

/** Loaded child rows -> editable rows with a stable local key. */
export const withKeys = (rows: Record<string, any>[]): ChildRow[] =>
  rows.map((r) => ({ ...r, _key: `r-${r.recno}` }));

/**
 * The child rows to POST: existing rows whose fields changed, `{recno, deleted:'Y'}` for
 * removed existing rows, and new rows (no recno). Unchanged rows and new-then-removed rows
 * are dropped. Only `fields` are posted (the backend 422s on unknown keys).
 */
export function childRowsForSave(
  original: Record<string, any>[], current: ChildRow[], fields: readonly string[],
): Record<string, any>[] {
  const byRecno = new Map<number, Record<string, any>>(original.map((r) => [Number(r.recno), r]));
  const out: Record<string, any>[] = [];
  for (const row of current) {
    const deleted = norm(row.deleted).toUpperCase() === 'Y';
    const picked: Record<string, any> = {};
    for (const f of fields) picked[f] = row[f] === null || row[f] === undefined ? '' : row[f];
    if (row.recno) {
      if (deleted) { out.push({ recno: row.recno, deleted: 'Y' }); continue; }
      const orig = byRecno.get(Number(row.recno)) ?? {};
      const changed = fields.some((f) => norm(orig[f]) !== norm(picked[f]));
      if (changed) out.push({ recno: row.recno, ...picked });
    } else if (!deleted) {
      out.push(picked);
    }
  }
  return out;
}

export const childrenDirty = (original: Record<string, any>[], current: ChildRow[], fields: readonly string[]): boolean =>
  childRowsForSave(original, current, fields).length > 0;
